"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { CheckCircle2, HelpCircle, MapPin, Truck, User } from "lucide-react";
import { cn, formatPrice } from "@/lib/utils";
import { getCountryName, getStateOrRegionName } from "@/lib/paypal-countries";
import type { EnrichedOrder } from "@/lib/orders/types";
import { resolveStorageUrl } from "@/lib/storage/public-url";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { SQUARE_SM, CARD_SM, imagePresetUrl } from "@/lib/images/presets";

type AddressLike = {
  address?: string;
  street?: string;
  street1?: string;
  apartment?: string;
  city?: string;
  state?: string;
  postal?: string;
  postalCode?: string;
  zip?: string;
  country?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
};

type Props = {
  order: EnrichedOrder;
  onContinue: () => void;
  onContact?: () => void;
  continueLabel?: string;
  className?: string;
};

function parseAddress(address: unknown): AddressLike {
  if (typeof address === "string") {
    try {
      const parsed = JSON.parse(address);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return address && typeof address === "object" ? (address as AddressLike) : {};
}

function cleanShippingLabel(method: unknown): string | null {
  if (!method || typeof method !== "object") return null;
  const name = "name" in method ? (method as { name?: unknown }).name : null;
  if (typeof name !== "string") return null;
  return name.replace(/\s*\(\$[\d.]+\)$|[\d.]+$/g, "").trim() || null;
}

function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function OrderPlaced({ order, onContinue, onContact, continueLabel, className }: Props) {
  const t = useTranslations("checkout");
  const router = useRouter();
  const address = parseAddress(order.address);
  const email = address.email || order.email || "";
  const firstName = address.firstName?.trim() || "";
  const displayName =
    address.name ||
    [address.firstName, address.lastName].filter(Boolean).join(" ").trim() ||
    email;
  const street = address.address || address.street || address.street1 || "";
  const postal = address.postal || address.postalCode || address.zip || "";
  const phone =
    address.phone ||
    [address.phoneCountryCode, address.phoneNumber].filter(Boolean).join("") ||
    order.phone ||
    "";
  const actionLabel = continueLabel || t("continueToMyOrders");

  const handleContact = () => {
    if (onContact) {
      onContact();
      return;
    }
    router.push("/contact");
  };

  return (
    <div className={cn("bg-neutral-900 px-4 pb-6 pt-5 text-white", className)}>
      <div className="flex flex-col items-center py-4 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10 ring-1 ring-green-500/25">
          <CheckCircle2 className="h-9 w-9 text-green-400" strokeWidth={1.75} />
        </div>
        <h2 className="mb-1.5 text-2xl font-bold text-white">
          {t("orderConfirmed", { suffix: firstName ? `, ${firstName}` : "" })}
        </h2>
        <p className="max-w-xs text-sm leading-relaxed text-neutral-400">
          {t.rich("confirmationEmailSent", {
            email: () => <span className="font-medium text-neutral-200">{email}</span>,
          })}
        </p>
      </div>

      <div className="space-y-3">
        <div className="overflow-hidden rounded-2xl bg-neutral-800">
          <div className="flex items-center justify-between border-b border-neutral-700 px-4 py-3">
            <span className="text-xs font-medium uppercase tracking-wider text-neutral-400">{t("order")}</span>
            <span className="font-mono text-xs text-neutral-300">{order.order_number}</span>
          </div>
          <div className="space-y-2.5 px-4 py-3">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-400">{t("subtotal")}</span>
              <span className="text-neutral-100">{formatPrice(order.subtotal, order.currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-400">{t("shipping")}</span>
              <span className="text-neutral-100">{formatPrice(order.shipping, order.currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-400">{t("serviceFees")}</span>
              <span className="text-neutral-100">{formatPrice(order.service_fee, order.currency)}</span>
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-neutral-700 px-4 py-3">
            <span className="text-sm font-semibold text-white">{t("total")}</span>
            <span className="font-bold text-white">{formatPrice(order.total, order.currency)}</span>
          </div>
        </div>

        {order.sellers.map((seller) => (
          <div key={seller.sellerOrderId} className="overflow-hidden rounded-2xl bg-neutral-800">
            <div className="flex items-center gap-2.5 border-b border-neutral-700 px-4 py-3">
              <div className="relative flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-700">
                {seller.avatar_url ? (
                  <Image
                    // `h-7 w-7` = 28 CSS px -> SQUARE_SM.
                    src={getAvatarUrl(seller.avatar_url, SQUARE_SM) ?? ""}
                    alt={seller.name}
                    fill
                    sizes="28px"
                    className="object-cover"
                  />
                ) : (
                  <User className="h-3.5 w-3.5 text-neutral-400" />
                )}
              </div>
              <span className="text-sm font-semibold text-white">{seller.name}</span>
            </div>

            <ul className="divide-y divide-neutral-700/50 px-4 pb-1 pt-3">
              {seller.items.map((item) => {
                const shippingLabel = cleanShippingLabel(item.selectedShippingMethod);
                const condition = item.listing_condition || null;
                const grade = item.listing_grade || null;
                // `order_items.image_url` may be a bucket-relative path.
                // `CARD_SM` and not `SQUARE_SM` for a 48 px box: the `<img>` below
                // renders `object-contain`, and `SQUARE_SM` is a square CDN-side
                // crop — asking for it would centre-crop order-confirmation
                // thumbnails that letterbox today. `CARD_SM` is the smallest
                // aspect-preserving rung, so the pixels on screen are unchanged
                // and the download drops from the full upload to ~13 KB.
                const resolvedImage = resolveStorageUrl(item.image_url) || null;
                const imageUrl = resolvedImage ? imagePresetUrl(resolvedImage, CARD_SM) : null;
                return (
                  <li key={item.id} className="flex items-start gap-2.5 py-2.5">
                    <div className="relative h-12 w-12 shrink-0 self-center overflow-hidden rounded-lg bg-neutral-750 ring-[1.5px] ring-neutral-700">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={item.name}
                          className="absolute inset-0 h-full w-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-neutral-700">
                          <User className="h-5 w-5 text-neutral-500" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-[15px] font-semibold leading-tight text-white">{item.name}</span>
                        <span className="shrink-0 text-[15px] font-semibold text-white">
                          {formatPrice(item.price * item.quantity, item.currency || order.currency)}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {condition ? (
                          <span className="rounded-full bg-neutral-700 px-2 py-0.5 text-xs font-medium text-neutral-300">
                            {capitalize(condition)}
                          </span>
                        ) : null}
                        {grade && condition?.toLowerCase() !== "new" ? (
                          <span className="rounded-full bg-neutral-700 px-2 py-0.5 text-xs font-medium text-neutral-300">
                            {grade}/10
                          </span>
                        ) : null}
                        {item.quantity > 1 ? (
                          <span className="rounded-full bg-neutral-700 px-2 py-0.5 text-xs font-medium text-neutral-300">
                            x{item.quantity}
                          </span>
                        ) : null}
                      </div>
                      {shippingLabel ? (
                        <div className="mt-1 flex items-center gap-1 text-neutral-400">
                          <Truck className="h-3 w-3 shrink-0" />
                          <span className="truncate text-[12px]">{shippingLabel}</span>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="rounded-2xl bg-neutral-800 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-700">
              <MapPin className="h-4 w-4 text-neutral-300" />
            </div>
            <div>
              <p className="mb-1 text-sm font-semibold text-white">{t("shippingAddress")}</p>
              <p className="text-sm leading-relaxed text-neutral-400">
                {displayName ? (
                  <>
                    {displayName}
                    <br />
                  </>
                ) : null}
                {street}
                {address.apartment ? (
                  <>
                    <br />
                    {address.apartment}
                  </>
                ) : null}
                <br />
                {address.city}
                {address.state && address.city ? ", " : ""}
                {address.state ? getStateOrRegionName(address.state, address.country || "") : ""}
                {postal && (address.city || address.state) ? " " : ""}
                {postal}
                {address.country ? (
                  <>
                    <br />
                    {getCountryName(address.country)}
                  </>
                ) : null}
                {email ? (
                  <>
                    <br />
                    {email}
                  </>
                ) : null}
                {phone ? (
                  <>
                    <br />
                    {phone}
                  </>
                ) : null}
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onContinue}
          className="w-full rounded-full bg-white px-4 py-3 text-sm font-semibold text-neutral-950 transition-colors hover:bg-neutral-200 active:bg-neutral-300"
        >
          {actionLabel}
        </button>

        <button
          type="button"
          onClick={handleContact}
          className="flex w-full items-center justify-center gap-2 rounded-full border border-neutral-700 py-2.5 text-sm text-neutral-400 transition-colors hover:bg-neutral-800 active:bg-neutral-800"
        >
          <HelpCircle className="h-4 w-4" />
          {t("needHelp")}
        </button>
      </div>
    </div>
  );
}
